/**
 * CTA (Button) Section Definition
 *
 * A call-to-action button section with full styling options.
 */

export const ctaSection = {
  type: 'cta',
  label: 'Button',
  icon: 'MousePointer',
  description: 'Button or link',
  category: 'section',

  defaultContent: {
    text: 'Continue',
    url: '',
    backgroundColor: '#4f46e5',
    textColor: '#ffffff',
    borderRadius: '8',
    buttonPadding: '12 24',
    fontSize: '16',
    fontWeight: 'medium',
    buttonSize: 'medium',
    buttonWidth: 'auto',
    customButtonWidth: '200px',
    textAlign: 'center',
    sectionBackgroundColor: 'transparent',
    padding: { base: { top: 0, right: 0, bottom: 0, left: 0, unit: 'px' }, tablet: null, desktop: null },
    margin: { base: { top: 0, right: 0, bottom: 16, left: 0, unit: 'px' }, tablet: null, desktop: null },
  },

  settingsSchema: [
    // Content category
    {
      key: 'text',
      type: 'text',
      label: 'Button Text',
      category: 'Content',
      placeholder: 'Continue',
    },
    {
      key: 'url',
      type: 'link',
      label: 'Link URL (optional)',
      category: 'Content',
      placeholder: 'https://example.com',
    },

    // Button Style category
    {
      key: 'buttonSize',
      type: 'select',
      label: 'Button Size',
      category: 'Button Style',
      default: 'medium',
      options: [
        { value: 'small', label: 'Small' },
        { value: 'medium', label: 'Medium' },
        { value: 'large', label: 'Large' },
      ],
    },
    {
      key: 'buttonWidth',
      type: 'select',
      label: 'Button Element Width',
      category: 'Button Style',
      default: 'auto',
      description: 'Controls the button element only',
      options: [
        { value: 'auto', label: 'Auto (fit content)' },
        { value: 'full', label: 'Full Width' },
        { value: 'custom', label: 'Custom' },
      ],
    },
    {
      key: 'customButtonWidth',
      type: 'text',
      label: 'Custom Button Width',
      category: 'Button Style',
      placeholder: '200px or 50%',
      default: '200px',
      description: 'Use px or %',
      showWhen: { field: 'buttonWidth', equals: 'custom' },
    },
    {
      key: 'borderRadius',
      type: 'number',
      label: 'Border Radius (px)',
      category: 'Button Style',
      min: 0,
      max: 50,
      default: '8',
    },

    // Colors category
    {
      key: 'backgroundColor',
      type: 'color',
      label: 'Button Background',
      category: 'Colors',
      default: '#4f46e5',
    },
    {
      key: 'textColor',
      type: 'color',
      label: 'Button Text Color',
      category: 'Colors',
      default: '#ffffff',
    },
    {
      key: 'sectionBackgroundColor',
      type: 'color',
      label: 'Section Background Color',
      category: 'Colors',
      default: 'transparent',
      allowTransparent: true,
      description: 'Background behind the entire button section',
    },

    // Style category
    {
      key: 'textAlign',
      type: 'select',
      label: 'Alignment',
      category: 'Style',
      default: 'center',
      options: [
        { value: 'left', label: 'Left' },
        { value: 'center', label: 'Center' },
        { value: 'right', label: 'Right' },
      ],
    },

    // Size category
    {
      key: 'width',
      type: 'responsiveDimension',
      label: 'Width',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'height',
      type: 'responsiveDimension',
      label: 'Height',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'minWidth',
      type: 'responsiveDimension',
      label: 'Min Width',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'minHeight',
      type: 'responsiveDimension',
      label: 'Min Height',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'maxWidth',
      type: 'responsiveDimension',
      label: 'Max Width',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'maxHeight',
      type: 'responsiveDimension',
      label: 'Max Height',
      category: 'Size',
      default: { base: { value: 'auto', unit: 'px' }, tablet: null, desktop: null },
    },

    // Border category
    {
      key: 'sectionBorderWidth',
      type: 'number',
      label: 'Border Width',
      category: 'Border',
      min: 0,
      max: 20,
      default: 0,
    },
    {
      key: 'sectionBorderStyle',
      type: 'select',
      label: 'Border Style',
      category: 'Border',
      default: 'solid',
      options: [
        { value: 'solid', label: 'Solid' },
        { value: 'dashed', label: 'Dashed' },
        { value: 'dotted', label: 'Dotted' },
        { value: 'none', label: 'None' },
      ],
    },
    {
      key: 'sectionBorderColor',
      type: 'color',
      label: 'Border Color',
      category: 'Border',
      default: 'transparent',
    },
    {
      key: 'sectionBorderRadius',
      type: 'number',
      label: 'Border Radius',
      category: 'Border',
      min: 0,
      max: 100,
      default: 0,
    },

    // Spacing category
    {
      key: 'padding',
      type: 'responsivePadding',
      label: 'Padding',
      category: 'Spacing',
      default: { base: { top: 0, right: 0, bottom: 0, left: 0, unit: 'px' }, tablet: null, desktop: null },
    },
    {
      key: 'margin',
      type: 'responsiveMargin',
      label: 'Margin',
      category: 'Spacing',
      default: { base: { top: 0, right: 0, bottom: 16, left: 0, unit: 'px' }, tablet: null, desktop: null },
    },
  ],
};

export default ctaSection;
